import { isEmpty, isNil } from 'lodash-es';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { FieldErrors } from 'react-hook-form';

interface TextAreaProps {
  id: string;
  label: string;
  type?: string;
  disabled?: boolean;
  formatPrice?: boolean;
  options?: any;
  register: any;
  getValues: any;
  setValue: any;
  errors: FieldErrors;
  maxLength?: number;
  onFocus?: () => void;
  onBlur?: () => void;
}

const TextArea: React.FC<TextAreaProps> = ({
  id,
  label,
  type = 'text',
  disabled,
  formatPrice,
  register,
  getValues,
  setValue,
  options,
  errors,
  maxLength,
  onFocus,
  onBlur,
}) => {
  const [text, setText] = useState<string | undefined>();

  const onTextChange = (value: any) => {
    let newText = undefined;

    if (!isNil(value)) {
      if (!isEmpty(value.target.value)) {
        const input = value.target.value;
        if (!isNil(maxLength)) {
          const truncatedValue = input.substring(
            0,
            Math.min(input.length, maxLength),
          );
          newText = truncatedValue;
        } else {
          newText = input;
        }
      } else {
        newText = '';
      }
    } else {
      newText = '';
    }

    if (!isNil(newText)) {
      setText(newText);
    }
  };

  useEffect(() => {
    const value: string = getValues(id);
    if (!isEmpty(value)) {
      setText(value);
    } else {
      setText('');
    }

    return () => {
      setText(undefined);
    };
  }, []);

  useEffect(() => {
    if (!isNil(text)) {
      //console.log('SET TEXT?', id, text);

      if (isEmpty(text)) {
        setValue(id, undefined);
      } else {
        setValue(id, text);
      }
    }
  }, [text, id, setValue]);

  return (
    <div className="grow min-w-full flex flex-col">
      <div className="relative grow min-w-full flex flex-col">
        <textarea
          data-test-id={`textarea-${id}`}
          id={id}
          disabled={disabled}
          {...register(id, options)}
          onChange={onTextChange}
          placeholder=" "
          type={type}
          className={`peer grow min-w-full resize-none items-start justify-start rounded-md border bg-white p-4 pt-6 text-start text-base font-light outline-none transition disabled:cursor-not-allowed disabled:opacity-70 ${
            formatPrice ? 'pl-9' : 'pl-4'
          } ${errors[id] ? 'border-red-300' : 'border-slate-200'} ${
            errors[id] ? 'focus:border-red-300' : 'focus:border-black'
          }`}
          onKeyDown={(e: any) => {
            if (e?.target && e?.key === 'Enter') {
              e.stopPropagation();
            }
          }}
          onKeyUp={(e: any) => {
            if (e?.target && e?.key === 'Enter') {
              e.stopPropagation();
            }
          }}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        <label
          htmlFor={id}
          className={`absolute start-1 top-1 z-10 origin-[0] -translate-y-4 scale-75 bg-white px-2 text-base leading-none duration-200 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:bg-transparent peer-focus:top-2 peer-focus:-translate-y-5 peer-focus:scale-75 peer-focus:bg-white peer-focus:px-2 peer-focus:text-zinc-600 rtl:peer-focus:left-auto rtl:peer-focus:translate-x-1/4 ${
            errors[id] ? 'text-red-500' : 'text-zinc-400'
          }`}
        >
          {!isEmpty(errors[id]?.message) &&
          typeof errors[id]?.message === 'string'
            ? `${errors[id]?.message}`
            : label}
        </label>
      </div>
      {maxLength && (
        <span className="flex min-w-full flex-row items-end justify-end">
          <span className="grow">&nbsp;</span>
          <span
            className={`text-xs font-semibold ${
              !isNil(text) && text.length > maxLength
                ? 'text-red-500'
                : 'text-slate-500'
            }`}
          >
            {!isNil(text) ? text.length : 0}/{maxLength}
          </span>
        </span>
      )}
    </div>
  );
};

export default TextArea;
